import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { fmtDate } from '../lib/fmt';

type Account = { id: string; code: string; name: string; account_type: string; system_tag: string | null };
type Period = { id: string; name: string; starts_on: string; ends_on: string; status: string };
type Line = { id: string; debit: number; credit: number; description: string | null; account: { code: string; name: string } | null };
type Entry = {
  id: string; entry_date: string; memo: string | null; source_type: string; currency: string; reverses_entry_id: string | null;
  journal: { code: string } | null; period: { name: string; status: string } | null; lines: Line[];
};

const TYPE_ORDER = ['asset', 'liability', 'equity', 'revenue', 'expense'];

export function Accounting() {
  const { t, i18n } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const [tab, setTab] = useState<'ledger' | 'chart' | 'periods'>('ledger');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const loc = i18n.language;
  const money = (n: number) => formatCurrency(n, currency, loc);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    const [acc, per, ent] = await Promise.all([
      supabase.from('chart_accounts').select('id,code,name,account_type,system_tag').is('deleted_at', null).order('code'),
      supabase.from('accounting_periods').select('id,name,starts_on,ends_on,status').is('deleted_at', null).order('starts_on', { ascending: false }),
      supabase.from('journal_entries').select('id,entry_date,memo,source_type,currency,reverses_entry_id,journal:accounting_journals(code),period:accounting_periods(name,status),lines:journal_entry_lines(id,debit,credit,description,account:chart_accounts(code,name))').is('deleted_at', null).order('entry_date', { ascending: false }).limit(200),
    ]);
    setAccounts((acc.data as any) ?? []);
    setPeriods((per.data as any) ?? []);
    setEntries((ent.data as any) ?? []);
    setLoading(false);
  }, [orgId]);
  useEffect(() => { void load(); }, [load]);

  const trialBalance = useMemo(() => {
    const m = new Map<string, { code: string; name: string; type: string; debit: number; credit: number }>();
    for (const e of entries) for (const l of e.lines ?? []) {
      const k = l.account?.code ?? '—';
      const acc = accounts.find((a) => a.code === l.account?.code);
      const row = m.get(k) ?? { code: k, name: l.account?.name ?? '', type: acc?.account_type ?? '', debit: 0, credit: 0 };
      row.debit += Number(l.debit); row.credit += Number(l.credit);
      m.set(k, row);
    }
    return [...m.values()].sort((a, b) => a.code.localeCompare(b.code));
  }, [entries, accounts]);

  const totals = useMemo(() => {
    let d = 0, c = 0;
    for (const r of trialBalance) { d += r.debit; c += r.credit; }
    return { d, c, balanced: Math.round((d - c) * 100) === 0 };
  }, [trialBalance]);

  async function importChart() {
    setBusy(true);
    await supabase.rpc('import_default_chart', { p_org: orgId });
    setBusy(false);
    await load();
  }
  async function closePeriod(id: string) {
    setBusy(true);
    await supabase.rpc('close_accounting_period', { p_period: id });
    setBusy(false);
    await load();
  }
  async function reopenPeriod(id: string) {
    setBusy(true);
    await supabase.rpc('reopen_accounting_period', { p_period: id });
    setBusy(false);
    await load();
  }
  async function reverse(id: string) {
    setBusy(true);
    await supabase.rpc('reverse_journal_entry', { p_entry: id, p_reason: null });
    setBusy(false);
    await load();
  }

  const reversedIds = useMemo(() => new Set(entries.filter((e) => e.reverses_entry_id).map((e) => e.reverses_entry_id)), [entries]);

  return (
    <Layout header={<Topbar actions={accounts.length === 0 ? <Button onClick={importChart} disabled={busy}>{t('acc.importChart')}</Button> : undefined} />}>
      <div className="px-7 py-6">
        <div className="mb-5 flex items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">{t('nav.accounting')}</h1>
            <p className="text-sm text-muted">{t('acc.subtitle')}</p>
          </div>
          <div className={cn('rounded-pill px-3 py-1 text-sm', totals.balanced ? 'bg-st-done-bg text-st-done-fg' : 'bg-st-overdue-bg text-st-overdue-fg')}>
            {t('acc.ledgerBalance')}: {money(totals.d)} {totals.balanced ? '✓' : '✗'}
          </div>
        </div>

        <div className="mb-5 flex gap-1 border-b border-border">
          {(['ledger', 'chart', 'periods'] as const).map((tb) => (
            <button key={tb} onClick={() => setTab(tb)} className={cn('px-4 py-2 text-sm', tab === tb ? 'border-b-2 border-accent-dark font-medium text-fg' : 'text-muted')}>
              {t('acc.tab_' + tb)}
            </button>
          ))}
        </div>

        {loading && <p className="text-sm text-muted">{t('common.loading')}</p>}

        {!loading && tab === 'ledger' && (
          <div className="space-y-6">
            <Card className="overflow-hidden p-0">
              <div className="border-b border-border px-4 py-2.5 text-sm font-medium">{t('acc.trialBalance')}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-muted"><th className="px-4 py-2">{t('acc.account')}</th><th className="px-4 py-2 text-right">{t('acc.debit')}</th><th className="px-4 py-2 text-right">{t('acc.credit')}</th></tr></thead>
                  <tbody>
                    {trialBalance.map((r) => (
                      <tr key={r.code} className="border-t border-divider">
                        <td className="px-4 py-1.5"><span className="font-mono text-xs text-muted">{r.code}</span> {r.name}</td>
                        <td className="px-4 py-1.5 text-right font-mono">{r.debit ? money(r.debit) : ''}</td>
                        <td className="px-4 py-1.5 text-right font-mono">{r.credit ? money(r.credit) : ''}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-border font-medium">
                      <td className="px-4 py-2">{t('acc.total')}</td>
                      <td className="px-4 py-2 text-right font-mono">{money(totals.d)}</td>
                      <td className="px-4 py-2 text-right font-mono">{money(totals.c)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>

            <Card className="overflow-hidden p-0">
              <div className="border-b border-border px-4 py-2.5 text-sm font-medium">{t('acc.journalEntries')} ({entries.length})</div>
              <div className="divide-y divide-divider">
                {entries.map((e) => {
                  const d = (e.lines ?? []).reduce((s, l) => s + Number(l.debit), 0);
                  const isReversed = reversedIds.has(e.id);
                  return (
                    <div key={e.id} className="px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-mono text-xs text-muted">{e.journal?.code}</span>
                            <span className="font-medium">{e.memo}</span>
                            {e.reverses_entry_id && <StatusPill tone="onhold">{t('acc.reversal')}</StatusPill>}
                            {isReversed && <StatusPill tone="overdue">{t('acc.reversed')}</StatusPill>}
                          </div>
                          <div className="text-xs text-muted">{fmtDate(e.entry_date, loc)} · {e.period?.name} · {money(d)}</div>
                        </div>
                        {!e.reverses_entry_id && !isReversed && e.period?.status === 'open' && (
                          <Button variant="ghost" onClick={() => reverse(e.id)} disabled={busy}>{t('acc.reverse')}</Button>
                        )}
                      </div>
                      <div className="mt-2 space-y-0.5 pl-2 text-xs">
                        {(e.lines ?? []).map((l) => (
                          <div key={l.id} className="flex justify-between gap-4 text-muted">
                            <span><span className="font-mono">{l.account?.code}</span> {l.account?.name}</span>
                            <span className="font-mono">{Number(l.debit) ? money(Number(l.debit)) : money(-Number(l.credit))}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {entries.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted">{t('acc.noEntries')}</div>}
              </div>
            </Card>
          </div>
        )}

        {!loading && tab === 'chart' && (
          <Card className="overflow-hidden p-0">
            {TYPE_ORDER.map((ty) => {
              const rows = accounts.filter((a) => a.account_type === ty);
              if (!rows.length) return null;
              return (
                <div key={ty}>
                  <div className="border-b border-divider bg-bg px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-muted">{t('acc.type_' + ty)}</div>
                  {rows.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 border-b border-divider px-4 py-2 text-sm">
                      <span className="w-14 font-mono text-xs text-muted">{a.code}</span>
                      <span className="flex-1">{a.name}</span>
                      {a.system_tag && <span className="rounded-pill bg-bg px-2 py-0.5 font-mono text-2xs text-muted">{a.system_tag}</span>}
                    </div>
                  ))}
                </div>
              );
            })}
            {accounts.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted">{t('acc.noChart')}</div>}
          </Card>
        )}

        {!loading && tab === 'periods' && (
          <Card className="overflow-hidden p-0">
            {periods.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 border-b border-divider px-4 py-3 text-sm">
                <div>
                  <span className="font-medium">{p.name}</span>
                  <span className="ml-2 text-xs text-muted">{fmtDate(p.starts_on, loc)} – {fmtDate(p.ends_on, loc)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill tone={p.status === 'closed' ? 'done' : 'open'}>{t('acc.period_' + p.status)}</StatusPill>
                  {p.status === 'open'
                    ? <Button variant="ghost" onClick={() => closePeriod(p.id)} disabled={busy}>{t('acc.close')}</Button>
                    : <Button variant="ghost" onClick={() => reopenPeriod(p.id)} disabled={busy}>{t('acc.reopen')}</Button>}
                </div>
              </div>
            ))}
            {periods.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted">{t('common.noData')}</div>}
          </Card>
        )}
      </div>
    </Layout>
  );
}
